import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CreateLeadData, LeadSource, Lead } from '@/types/lead.types';
import { leadsService } from '@/services/leads.service';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

const sourceOptions: { value: LeadSource; label: string }[] = [
  { value: 'WEBSITE', label: 'Website' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'META', label: 'Meta' },
  { value: 'GOOGLE', label: 'Google' },
  { value: 'EXHIBITION', label: 'Exhibition' },
  { value: 'MANUAL', label: 'Manual Entry' },
  { value: 'REFERRAL', label: 'Referral' },
];

interface FormErrors {
  name?: string;
  phone?: string;
  email?: string;
  source?: string;
  budget_min?: string;
  budget_max?: string;
}

export const AddLead: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<CreateLeadData>({
    name: '',
    phone: '',
    email: '',
    source: 'MANUAL',
    project_interest: '',
    budget_min: undefined,
    budget_max: undefined,
    notes: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateLead, setDuplicateLead] = useState<Lead | null>(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  // Phone number validation (Indian format)
  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^(\+91[\s-]?)?[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/[\s-]/g, ''));
  };

  // Email validation
  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check for duplicate phone
  const checkDuplicate = useCallback(async (phone: string) => {
    if (!phone || !validatePhone(phone)) {
      setDuplicateLead(null);
      return;
    }

    try {
      setIsCheckingDuplicate(true);
      const response = await leadsService.checkDuplicate(phone);
      if (response.data?.isDuplicate && response.data?.existingLead) {
        setDuplicateLead(response.data.existingLead as Lead);
      } else {
        setDuplicateLead(null);
      }
    } catch (err) {
      console.error('Error checking duplicate:', err);
      setDuplicateLead(null);
    } finally {
      setIsCheckingDuplicate(false);
    }
  }, []);

  // Debounce phone duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.phone.length >= 10) {
        checkDuplicate(formData.phone);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.phone, checkDuplicate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // Handle number fields
    if (name === 'budget_min' || name === 'budget_max') {
      setFormData({
        ...formData,
        [name]: value ? Number(value) : undefined,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Enter a valid Indian phone number';
    }

    // Email validation
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    // Budget validation
    if (formData.budget_min && formData.budget_max && formData.budget_min > formData.budget_max) {
      newErrors.budget_max = 'Max budget must be greater than min budget';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setIsSubmitting(true);

      // Clean up the data
      const submitData: CreateLeadData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        source: formData.source,
      };

      if (formData.email?.trim()) {
        submitData.email = formData.email.trim();
      }
      if (formData.project_interest?.trim()) {
        submitData.project_interest = formData.project_interest.trim();
      }
      if (formData.budget_min) {
        submitData.budget_min = formData.budget_min;
      }
      if (formData.budget_max) {
        submitData.budget_max = formData.budget_max;
      }
      if (formData.notes?.trim()) {
        submitData.notes = formData.notes.trim();
      }

      const response = await leadsService.create(submitData);
      // API returns { lead: {...} }
      if (response.data?.lead?.id) {
        navigate(`/leads/${response.data.lead.id}`);
      } else {
        console.error('Lead created but no ID returned:', response.data);
        navigate('/leads');
      }
    } catch (err: unknown) {
      console.error('Error creating lead:', err);
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(error?.response?.data?.message || 'Failed to create lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatBudget = (value: number | undefined): string => {
    if (!value) return '';
    if (value >= 10000000) {
      return `${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `${(value / 100000).toFixed(2)} L`;
    }
    return value.toLocaleString('en-IN');
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/leads')}
          className="inline-flex items-center text-sm text-secondary-600 hover:text-secondary-900 mb-4"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Leads
        </button>
        <h1 className="text-2xl font-bold text-secondary-900">Add New Lead</h1>
        <p className="text-secondary-600 mt-1">Fill in the details to create a new lead</p>
      </div>

      {/* Duplicate Warning */}
      {duplicateLead && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-amber-800">Duplicate Phone Number</h4>
              <p className="text-sm text-amber-700 mt-1">
                A lead with this phone number already exists:
              </p>
              <button
                onClick={() => navigate(`/leads/${duplicateLead.id}`)}
                className="mt-2 text-sm font-medium text-amber-800 underline hover:no-underline"
              >
                {duplicateLead.name} (#{duplicateLead.lead_number})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <Input
            label="Name"
            name="name"
            placeholder="Enter lead name"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            required
          />

          {/* Phone */}
          <div>
            <Input
              label="Phone Number"
              name="phone"
              type="tel"
              placeholder="+91 9876543210"
              value={formData.phone}
              onChange={handleChange}
              error={errors.phone}
              required
              rightIcon={
                isCheckingDuplicate ? (
                  <div className="w-5 h-5 border-2 border-secondary-200 border-t-primary-600 rounded-full animate-spin"></div>
                ) : duplicateLead ? (
                  <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                ) : formData.phone.length >= 10 && validatePhone(formData.phone) ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : null
              }
            />
            <p className="mt-1 text-xs text-secondary-500">Indian phone number format</p>
          </div>

          {/* Email */}
          <Input
            label="Email (Optional)"
            name="email"
            type="email"
            placeholder="email@example.com"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          />

          {/* Source */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Lead Source
            </label>
            <select
              name="source"
              value={formData.source}
              onChange={handleChange}
              className="block w-full rounded-lg border border-secondary-300 bg-white px-4 py-2.5 text-secondary-900 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-primary-500 focus:ring-primary-500"
            >
              {sourceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Project Interest */}
          <Input
            label="Project Interest (Optional)"
            name="project_interest"
            placeholder="e.g., 2BHK in South Mumbai"
            value={formData.project_interest}
            onChange={handleChange}
          />

          {/* Budget Range */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Budget Range (Optional)
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  name="budget_min"
                  type="number"
                  placeholder="Min"
                  value={formData.budget_min || ''}
                  onChange={handleChange}
                  error={errors.budget_min}
                />
                {formData.budget_min && (
                  <p className="mt-1 text-xs text-secondary-500">
                    {formatBudget(formData.budget_min)}
                  </p>
                )}
              </div>
              <div>
                <Input
                  name="budget_max"
                  type="number"
                  placeholder="Max"
                  value={formData.budget_max || ''}
                  onChange={handleChange}
                  error={errors.budget_max}
                />
                {formData.budget_max && (
                  <p className="mt-1 text-xs text-secondary-500">
                    {formatBudget(formData.budget_max)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Notes (Optional)
            </label>
            <textarea
              name="notes"
              rows={4}
              className="block w-full rounded-lg border border-secondary-300 bg-white px-4 py-2.5 text-secondary-900 placeholder-secondary-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-primary-500 focus:ring-primary-500 resize-none"
              placeholder="Add any additional notes..."
              value={formData.notes}
              onChange={handleChange}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-200">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/leads')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={!!duplicateLead}
            >
              Create Lead
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddLead;
